import React, {
  useState,
  useContext,
  useCallback,
  createContext,
  type ReactNode,
} from 'react'

import {toast} from 'react-toastify'

import {useCheckout} from '@festi/common/contexts'
import {PaymentGateway} from '@festi/common/api/rest'

import {paymentGatewayFetch} from './api/fetch'

export type PagoUserState =
  | 'NO_REGISTRATION'
  | 'TERMS_NOT_ACCEPTED'
  | 'CREDIT_CHECK_NOT_VALID'
  | 'NEEDS_TO_PERFORM_KYC'
  | 'KYC_REJECTED'
  | 'KYC_PENDING'
  | 'ELIGABLE'
  | 'ERROR' // TODO: Do we need this?
  | 'BAD_GATEWAY' // TODO: Do we need this?

export type KycQuestion = {
  id: number
  question: string
  answers: string[]
  type: 'multiple' | 'single'
}

export type KycApiAnswer = {
  questionId: number
  answer: string | string[]
}

export type KycAnswer = Record<number, string | string[]>

export const PagoContext = createContext<PagoContextProps | undefined>(
  undefined,
)

interface PagoProviderProps {
  children: ReactNode
}

export function PagoProvider({children}: PagoProviderProps) {
  const {restCheckout} = useCheckout()

  const [loading, setLoading] = useState<boolean>(false)
  const [termsUrl, setTermsUrl] = useState<string>('')
  const [kycQuestions, setKycQuestions] = useState<KycQuestion[]>(null)
  const [pagoCreditLimit, setPagoCreditLimit] = useState<number | null>(null)
  const [pagoUserStatus, setPagoUserStatus] =
    useState<PagoUserState>('NO_REGISTRATION')

  const pagoGateway = restCheckout?.availablePaymentGateways.find((gateway) =>
    gateway.id.includes('pago'),
  )

  const handleRegistrationUrl = useCallback(
    (
      method: 'GET' | 'POST',
      body?: Record<string, string | boolean | number | KycApiAnswer[]>,
      query?: string,
    ) => {
      setLoading(true)

      return paymentGatewayFetch(
        query
          ? `${pagoGateway.urls.registration}?${query}`
          : pagoGateway.urls.registration,
        method,
        method === 'POST' && body,
      )
        .then((data) => {
          console.log('data', data)
          if (data?.creditLimit) {
            setPagoCreditLimit(data.creditLimit)
          } else {
            setPagoCreditLimit(null)
          }
          if (!!data?.error) {
            setPagoUserStatus(data.error)
          } else {
            setPagoUserStatus('ELIGABLE')
          }

          if (data?.questions) {
            setKycQuestions(data.questions)
          } else {
            setKycQuestions(null)
          }

          if (data?.signingUrl) {
            setTermsUrl(data.signingUrl)
          } else {
            setTermsUrl('')
          }
          return data
        })
        .catch((error) => {
          console.error('Error:', error)
          // TODO: Is this the best way to handle these errors?
          toast.error(`Villa hjá Snjallgreiðslur: ${error.toString()}`)
          setPagoUserStatus('ERROR')
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [pagoGateway?.urls.registration, setKycQuestions, setPagoUserStatus],
  )

  return (
    <PagoContext.Provider
      value={{
        loading,
        termsUrl,
        pagoGateway,
        kycQuestions,
        pagoUserStatus,
        pagoCreditLimit,
        setTermsUrl,
        setKycQuestions,
        setPagoUserStatus,
        handleRegistrationUrl,
      }}
    >
      {children}
    </PagoContext.Provider>
  )
}

export interface PagoContextProps {
  loading: boolean
  termsUrl: string
  pagoGateway: PaymentGateway
  kycQuestions: KycQuestion[] | null
  pagoUserStatus: PagoUserState
  pagoCreditLimit: number | null
  setPagoUserStatus: React.Dispatch<React.SetStateAction<PagoUserState>>
  setKycQuestions: React.Dispatch<React.SetStateAction<KycQuestion[] | null>>
  handleRegistrationUrl: (
    method: 'GET' | 'POST',
    body?: Record<string, string | boolean | number | KycApiAnswer[]>,
    query?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>
  setTermsUrl: React.Dispatch<React.SetStateAction<string>>
}

export const usePago = (): PagoContextProps => {
  const context = useContext(PagoContext)
  if (!context) {
    throw new Error('usePago must be used within a PagoProvider')
  }
  return context
}
